import { addDays, format, parseISO, subDays } from "date-fns";
import { formatDateRangePt } from "../../../dashboard-framework/describeWidgetLogic";
import { EXCLUDE_INTERNAL_SESSIONS_FILTER } from "../../../dashboard-framework/commonFilters";
import { DateFilterKey, DateRange } from "../../../dashboard-framework/types";
import { ROLES } from "../../../utils/constants";

// Pedido real do stakeholder (Jorge, 13/06/2026) — critérios e decisões validadas em spec.md.
// Tudo client-side a partir de fetches raw: a métrica soma DURAÇÕES de sessões/horário (não há
// helper no framework) e o rollup parte de três coleções (schedules + sessions + evaluations).
// Ajuste 17/07/2026: passa a considerar QUALQUER serviço (não só NFB/EEG) e todos os colaboradores
// com horário e/ou sessões feitas; a análise por função faz-se com o filtro "Função" (client-side).

export const NEUROFEEDBACK_PRODUCTIVITY_KEY = "neurofeedback-productivity";

// Filtro "Função" — mesma classificação da dashboard "Tarefas" (agrupamento dos schedules):
// especialização de técnico para Neurofeedback/Psicologia, role para Apoio ao cliente, resto Outros.
// Cópia LOCAL (não partilhada) para não alterar o comportamento da team-tasks — ver README/memória
// "scope-to-current-dashboard". Candidata a extração para shared/ num passo dedicado.
export type NfbFunctionKey = "neurofeedback" | "psicologia" | "apoio_cliente" | "outros";
export type NfbFunctionFilterKey = "todas" | NfbFunctionKey;

export const CUSTOMER_SUPPORT_ROLE_IDS: Array<string> = [
    ROLES.recepcionist,
    ROLES.reception_director,
    ROLES.callcenter,
    ROLES.back_office,
];

export const FUNCTION_OPTIONS: Array<{ key: NfbFunctionKey; label: string }> = [
    { key: "neurofeedback", label: "Neurofeedback" },
    { key: "psicologia", label: "Psicologia" },
    { key: "apoio_cliente", label: "Apoio ao cliente" },
    { key: "outros", label: "Outros" },
];

// Subset de presets do filtro partilhado (DashboardDateFilter acrescenta sempre "Personalizado").
// Variantes "até ontem": a métrica compara sessões feitas ÷ horário do staff, e dias por cumprir
// (hoje/futuro) não têm sessões feitas — incluí-los enviesaria o rácio, por isso o período pára ontem.
export const NFB_PERIOD_PRESETS: Array<DateFilterKey> = [
    "ultimos_30_dias_ate_ontem",
    "ultimos_7_dias_ate_ontem",
    "esta_semana_ate_ontem",
    "este_mes_ate_ontem",
];

const formatDay = (date: Date) => {
    const formatted = format(date, "yyyy-MM-dd");
    return formatted;
};

// A janela de fetch é alargada 1 dia de cada lado: o recorte fino é feito client-side pelo dia
// em fuso de Lisboa (lisbonDayKey), e uma sessão perto da meia-noite UTC pode cair no dia de
// Lisboa adjacente. Alargar garante que nenhuma linha de fronteira é perdida antes do recorte.
export const widenRangeForFetch = (range: DateRange): DateRange => {
    const widened = { gte: formatDay(subDays(parseISO(range.gte), 1)), lt: formatDay(addDays(parseISO(range.lt), 1)) };
    return widened;
};

// Campos sempre enumerados (nunca wildcards). Relações M2O devolvem o id; pedimos .user_id para
// fazer a ponte technicians → directus_users (a chave de rollup). role serve para classificar a função.
export const TECHNICIANS_FIELDS = "id,user_id,specializations";
export const SCHEDULES_FIELDS = "id,start_date,end_date,user";
export const DONE_SESSIONS_FIELDS = "id,start_date,end_date,technician.user_id";
export const INTERNAL_SESSIONS_FIELDS = "id,start_date,end_date,internal_meetings_participants.technicians_id.user_id";
export const EVALUATIONS_FIELDS = "id,brain_maps_date,brain_maps_technician.user_id";
export const PEOPLE_FIELDS = "id,first_name,last_name,email,role";

// /users ignora limit/page (createRoute) e responde com ≤100 — lookup em blocos abaixo do teto.
export const PEOPLE_LOOKUP_CHUNK_SIZE = 80;

const dateRangeClause = (field: string, range: DateRange) => {
    const clause = { [field]: { _gte: range.gte, _lt: range.lt } };
    return clause;
};

// Vista completa: sem restrição de utilizador (todos os colaboradores). Vista pessoal
// (viewer_rules → technicianUserId): recorta a UM utilizador pelo user_id.
const restrictToUserClause = (relationPath: string, restrictToUserId: string | null) => {
    if (!restrictToUserId) return null;
    const clause = { [relationPath]: { user_id: { _eq: restrictToUserId } } };
    return clause;
};

// Tempo em sessão = SÓ sessões de cliente realizadas (qualquer serviço). A exclusão de sessões
// internas é OBRIGATÓRIA (há sessões done marcadas como internas que, sem isto, contariam em
// sessão E reuniões). Não há filtro de serviço: considera-se qualquer serviço.
export const doneClientSessionsFilter = (fetchRange: DateRange, restrictToUserId: string | null) => {
    const clauses: Array<Record<string, unknown>> = [
        { status: { _eq: "done" } },
        EXCLUDE_INTERNAL_SESSIONS_FILTER,
        dateRangeClause("start_date", fetchRange),
    ];
    const userClause = restrictToUserClause("technician", restrictToUserId);
    if (userClause) clauses.push(userClause);
    const filter = { _and: clauses };
    return filter;
};

// Reuniões/formações de equipa = sessões COM participantes internos (internal_meetings_participants).
// NÃO se usa is_team_internal_session: esse campo é incoerente (reuniões reais aparecem a false); a
// lista de participantes é o sinal fiável de quem esteve presente. Creditadas client-side. Sem
// recorte de utilizador — na vista pessoal a linha é filtrada depois (o utilizador só vê a sua).
export const internalSessionsFilter = (fetchRange: DateRange) => {
    const filter = {
        _and: [{ internal_meetings_participants: { technicians_id: { _nnull: true } } }, dateRangeClause("start_date", fetchRange)],
    };
    return filter;
};

export const schedulesFilter = (fetchRange: DateRange, restrictToUserId: string | null) => {
    const clauses: Array<Record<string, unknown>> = [dateRangeClause("start_date", fetchRange)];
    if (restrictToUserId) clauses.push({ user: { _eq: restrictToUserId } });
    const filter = { _and: clauses };
    return filter;
};

export const brainMapsFilter = (fetchRange: DateRange, restrictToUserId: string | null) => {
    const clauses: Array<Record<string, unknown>> = [dateRangeClause("brain_maps_date", fetchRange)];
    const userClause = restrictToUserClause("brain_maps_technician", restrictToUserId);
    if (userClause) clauses.push(userClause);
    const filter = { _and: clauses };
    return filter;
};

// Drill da linha: as mesmas sessões de cliente realizadas que originam o Tempo em sessão do
// colaborador (qualquer serviço — sem filtro de serviço, para bater com o número da linha).
export const technicianDoneSessionsDrillFilter = (fetchRange: DateRange, viewerUserId: string) => {
    const filter = {
        _and: [
            { status: { _eq: "done" } },
            EXCLUDE_INTERNAL_SESSIONS_FILTER,
            { technician: { user_id: { _eq: viewerUserId } } },
            dateRangeClause("start_date", fetchRange),
        ],
    };
    return filter;
};

const functionScopeText = (functionFilter: NfbFunctionFilterKey) => {
    if (functionFilter === "todas") return "";
    const label = FUNCTION_OPTIONS.find((option) => option.key === functionFilter)?.label || functionFilter;
    const text = ` da função "${label}"`;
    return text;
};

// Explicação ⓘ — derivada do MESMO período/filtro da query (texto gerado, nunca solto).
export const describeNeurofeedbackTablePt = (range: DateRange, isOwnView: boolean, functionFilter: NfbFunctionFilterKey) => {
    const base =
        `Uma linha por técnico${functionScopeText(functionFilter)} com atividade ${formatDateRangePt(range)} (datas em fuso de Lisboa). ` +
        `Apenas colaboradores com registo na coleção de técnicos — receção/backoffice sem ficha de técnico ficam de fora. ` +
        `Horário lançado = soma de todos os blocos de horário lançados no período (qualquer serviço, incluindo backoffice). ` +
        `Tempo em sessão = duração das sessões de cliente realizadas (qualquer serviço). ` +
        `Reuniões/Formações = duração das reuniões/formações internas em que o colaborador consta como participante. ` +
        `Tempo sem sessão = Horário lançado − Tempo em sessão − Reuniões/Formações (nunca negativo). ` +
        `% Tempo sem sessão = Tempo sem sessão ÷ Horário lançado — a métrica em foco, a aproximar de 0 (tabela ordenada por ela, decrescente). ` +
        `Tempo médio sem sessão por dia = Tempo sem sessão ÷ nº de dias com horário lançado no período (só os dias em que o colaborador trabalhou, não os dias do período; "—" sem horário). ` +
        `Brain maps = nº de mapas cerebrais cuja data cai no período (métrica de neurofeedback; 0 para outras funções). ` +
        `Brain maps por hora = brain maps ÷ horas de tempo sem sessão (uma casa decimal; "—" quando não há tempo sem sessão).`;
    const description = isOwnView ? `${base} Vista pessoal: só a tua linha.` : base;
    return description;
};

// Formatação de horas "Xh YYm" (igual ao mockup aprovado) — determinística, sem toFixed.
export const formatHours = (hours: number) => {
    const safeHours = Number.isFinite(hours) && hours > 0 ? hours : 0;
    const totalMinutes = Math.round(safeHours * 60);
    const wholeHours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const formatted = `${wholeHours}h ${String(minutes).padStart(2, "0")}m`;
    return formatted;
};
